import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { bindActionCreators } from 'redux'

import { Search, X, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react'

import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Alert } from '@/components/ui/alert'
import { Spinner } from '@/components/ui/spinner'
import { DataTable } from '@/components/ui/data-table'

import {
  AIRDATE_COLUMN,
  COMPLETION_DUE_DATE_COLUMN,
  ORDER_NAME_COLUMN,
  ACCOUNT_NAME_COLUMN,
  ORDER_STATUS_COLUMN,
  EPISODE_AND_SOURCE_FILENAME_COLUMN,
  PROGRAM_VERSION_COLUMN,
  ORDER_TYPE_COLUMN,
  ORDER_NAME_COLUMN_FRIENDLY,
  ACCOUNT_NAME_COLUMN_FRIENDLY,
  AIRDATE_COLUMN_FRIENDLY,
  COMPLETION_DUE_DATE_COLUMN_FRIENDLY,
  ORDER_STATUS_COLUMN_FRIENDLY,
  EPISODE_AND_SOURCE_FILENAME_COLUMN_FRIENDLY,
  PROGRAM_VERSION_COLUMN_FRIENDLY,
  ORDER_TYPE_COLUMN_FRIENDLY,
  requestOrders,
  requestSearch,
  requestSortOrders,
  requestPageStep
} from '../../stores/orders'

import { requestNavigation } from '../../stores/auth'

import './index.css'

function orderStatusVariant(status) {
  if (!status) return 'secondary'
  const s = status.toLowerCase()
  if (s.includes('complete')) return 'success'
  if (s.includes('progress') || s.includes('active') || s.includes('process')) return 'default'
  if (s.includes('cancel')) return 'destructive'
  if (s.includes('hold') || s.includes('pending')) return 'warning'
  return 'secondary'
}

const fmtDate = (val, utc = false) => {
  if (!val) return ''
  return new Date(val).toLocaleDateString('en-US', utc ? { timeZone: 'UTC' } : undefined)
}

function ViewAllSalesOrders({
  fetched,
  ordersBelongToPrimaryAccount,
  ordersAreBothLiveAndPost,
  totalSubsetOrders,
  subsetOrders,
  loading,
  error,
  hasError,
  sortColumn,
  sortDirection,
  searchTerm,
  pageNumber,
  numberOfPages,
  requestOrders,
  requestSortOrders,
  requestSearch,
  requestPageStep,
  requestNavigation,
}) {
  useEffect(() => {
    if (!fetched) requestOrders()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Local input state so the field feels instant; Redux dispatch is debounced 250ms
  const [localSearch, setLocalSearch] = useState(searchTerm)
  const debounceTimer = useRef(null)

  const handleSearchChange = useCallback(value => {
    setLocalSearch(value)
    clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      requestSearch(value)
    }, 250)
  }, [requestSearch])

  const handleSearchClear = useCallback(() => {
    setLocalSearch('')
    clearTimeout(debounceTimer.current)
    requestSearch('')
  }, [requestSearch])

  // Keep local state in sync when store resets (e.g. navigate away and back)
  useEffect(() => {
    setLocalSearch(searchTerm)
  }, [searchTerm])

  // Bridge Redux sort state <-> TanStack sorting (sort is server/Redux-side)
  const sorting = useMemo(
    () => (sortColumn ? [{ id: sortColumn, desc: (sortDirection[sortColumn] || 'asc') === 'desc' }] : []),
    [sortColumn, sortDirection]
  )
  const handleSortingChange = updater => {
    const next = typeof updater === 'function' ? updater(sorting) : updater
    if (!next.length) return
    requestSortOrders(next[0].id, next[0].desc ? 'desc' : 'asc')
  }

  const columns = useMemo(() => {
    const linkCell = ({ row }) => (
      <button
        type="button"
        onClick={() => requestNavigation(`/orders/${row.original.id}`)}
        className="font-medium text-primary hover:underline"
      >
        {row.original[ORDER_NAME_COLUMN]}
      </button>
    )
    const truncCell = key => ({ getValue }) => {
      const v = getValue() || ''
      return <span title={v}>{v}</span>
    }
    return [
      {
        id: '#',
        header: '#',
        accessorKey: '#',
        size: 38,
        enableSorting: false,
        enableResizing: false,
        meta: { reorderable: false, align: 'right', cellClassName: 'text-muted-foreground' },
      },
      {
        id: ORDER_NAME_COLUMN,
        header: 'Order #',
        accessorKey: ORDER_NAME_COLUMN,
        cell: linkCell,
        size: 110,
        meta: { headerTitle: ORDER_NAME_COLUMN_FRIENDLY },
      },
      ...(!ordersBelongToPrimaryAccount
        ? [{
            id: ACCOUNT_NAME_COLUMN,
            header: 'Account',
            accessorKey: ACCOUNT_NAME_COLUMN,
            cell: truncCell(ACCOUNT_NAME_COLUMN),
            size: 150,
            meta: { headerTitle: ACCOUNT_NAME_COLUMN_FRIENDLY },
          }]
        : []),
      ...(ordersAreBothLiveAndPost
        ? [{
            id: ORDER_TYPE_COLUMN,
            header: 'Type',
            accessorKey: ORDER_TYPE_COLUMN,
            cell: truncCell(ORDER_TYPE_COLUMN),
            size: 80,
            meta: { headerTitle: ORDER_TYPE_COLUMN_FRIENDLY },
          }]
        : []),
      {
        id: EPISODE_AND_SOURCE_FILENAME_COLUMN,
        header: 'Episode / Source',
        accessorKey: EPISODE_AND_SOURCE_FILENAME_COLUMN,
        cell: truncCell(EPISODE_AND_SOURCE_FILENAME_COLUMN),
        size: 320,
        meta: { headerTitle: EPISODE_AND_SOURCE_FILENAME_COLUMN_FRIENDLY },
      },
      {
        id: PROGRAM_VERSION_COLUMN,
        header: 'Program',
        accessorKey: PROGRAM_VERSION_COLUMN,
        cell: truncCell(PROGRAM_VERSION_COLUMN),
        size: 280,
        meta: { headerTitle: PROGRAM_VERSION_COLUMN_FRIENDLY },
      },
      {
        id: ORDER_STATUS_COLUMN,
        header: 'Status',
        accessorKey: ORDER_STATUS_COLUMN,
        size: 130,
        cell: ({ getValue }) => {
          const v = getValue()
          return v ? <Badge variant={orderStatusVariant(v)}>{v}</Badge> : null
        },
        meta: { headerTitle: ORDER_STATUS_COLUMN_FRIENDLY },
      },
      {
        id: COMPLETION_DUE_DATE_COLUMN,
        header: 'Due',
        accessorKey: COMPLETION_DUE_DATE_COLUMN,
        size: 100,
        cell: ({ getValue }) => fmtDate(getValue()),
        meta: { headerTitle: COMPLETION_DUE_DATE_COLUMN_FRIENDLY },
      },
      {
        id: AIRDATE_COLUMN,
        header: 'Air Date',
        accessorKey: AIRDATE_COLUMN,
        size: 100,
        cell: ({ getValue }) => fmtDate(getValue(), true),
        meta: { headerTitle: AIRDATE_COLUMN_FRIENDLY },
      },
    ]
  }, [ordersBelongToPrimaryAccount, ordersAreBothLiveAndPost, requestNavigation])

  return (
    <div className="space-y-3">
      {hasError && <Alert variant="error">{error}</Alert>}

      <Card className="overflow-hidden">
        {/* Header toolbar */}
        <div className="flex flex-wrap items-center gap-3 border-b px-3 py-2">
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold leading-tight">Orders</h2>
            <p className="text-xs text-muted-foreground">
              {`${totalSubsetOrders} total · ${subsetOrders.length} shown · Page ${pageNumber}/${Math.max(numberOfPages, 1)}`}
              {searchTerm ? ` · "${searchTerm}"` : ''}
            </p>
          </div>

          <div className="relative w-56">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search…"
              value={localSearch}
              onChange={e => handleSearchChange(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') handleSearchClear() }}
              className="h-8 pl-8 pr-8"
            />
            {localSearch.length > 0 && (
              <button
                onClick={handleSearchClear}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <Button
            variant="outline"
            size="icon-sm"
            onClick={requestOrders}
            disabled={loading}
            title="Refresh"
          >
            {loading ? <Spinner size={16} /> : <RefreshCw className="h-4 w-4" />}
          </Button>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon-sm"
              onClick={() => requestPageStep(pageNumber - 1)}
              disabled={pageNumber <= 1}
              title="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Input
              type="number"
              value={numberOfPages > 0 ? pageNumber : 1}
              onChange={e => requestPageStep(parseInt(e.target.value) || 1)}
              onFocus={e => e.target.select()}
              min={1}
              max={Math.max(numberOfPages, 1)}
              className="h-8 w-14 text-center"
            />
            <span className="whitespace-nowrap text-sm text-muted-foreground">
              / {Math.max(numberOfPages, 1)}
            </span>
            <Button
              variant="outline"
              size="icon-sm"
              onClick={() => requestPageStep(pageNumber + 1)}
              disabled={pageNumber >= numberOfPages}
              title="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <DataTable
          tableId="orders"
          columns={columns}
          data={subsetOrders}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          manualSorting
          enableSortingRemoval={false}
          getRowId={(row, i) => row.id || String(i)}
          isLoading={loading}
          emptyMessage="No orders found"
        />

        {/* Bottom pagination */}
        <div className="flex items-center justify-center gap-2 border-t px-3 py-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => requestPageStep(pageNumber - 1)}
            disabled={pageNumber <= 1}
          >
            <ChevronLeft className="h-4 w-4" /> Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => requestPageStep(pageNumber + 1)}
            disabled={pageNumber >= numberOfPages}
          >
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </Card>
    </div>
  )
}

const mapStateToProps = (state, route) => {
  const {
    fetched,
    ordersBelongToPrimaryAccount,
    ordersAreBothLiveAndPost,
    orders,
    totalSubsetOrders,
    subsetOrders,
    loading,
    error,
    hasError,
    pageNumber,
    pageSize,
    numberOfPages,
    sortColumn,
    sortDirection,
    searchTerm,
  } = state.stores.orders

  return {
    ...route.match.params,
    fetched,
    ordersBelongToPrimaryAccount,
    ordersAreBothLiveAndPost,
    orders,
    totalSubsetOrders,
    subsetOrders,
    loading,
    error,
    hasError,
    pageNumber,
    pageSize,
    numberOfPages,
    sortColumn,
    sortDirection,
    searchTerm,
  }
}

const mapDispatchToProps = dispatch => ({
  ...bindActionCreators({
    requestOrders,
    requestSortOrders,
    requestSearch,
    requestPageStep,
    requestNavigation,
  }, dispatch)
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ViewAllSalesOrders))
